'use client';

import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType } from 'embla-carousel';
import ArrowLeft from '@/shared/components/primitives/svg/arrow-left';
import ArrowRight from '@/shared/components/primitives/svg/arrow-right';
import { cn } from '@/shared/utils/cn';
import Autoplay from 'embla-carousel-autoplay';

export const HeroCarouselControls = ({ children }: PropsWithChildren): React.ReactElement => {
  const [viewportRef, api] = useEmblaCarousel(
    {
      loop: true,
      axis: 'x',
      align: 'start',
    },
    [Autoplay({ playOnInit: true, delay: 5000, stopOnMouseEnter: true, stopOnInteraction: false })],
  );

  const slides = useMemo(() => React.Children.toArray(children), [children]);

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selectedSnapIndex, setSelectedSnapIndex] = useState(0);

  const onSelect = useCallback((embla: EmblaCarouselType) => {
    if (!embla) return;
    setCanPrev(embla.canScrollPrev());
    setCanNext(embla.canScrollNext());
    setSelectedSnapIndex(embla.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!api) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onSelect(api);
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api, onSelect]);

  const handlePrev = useCallback(() => api?.scrollPrev(), [api]);
  const handleNext = useCallback(() => api?.scrollNext(), [api]);

  return (
    <div className='relative' aria-roledescription='carousel' role='region'>
      {/* Left Arrow */}
      <button
        type='button'
        aria-label='Previous slide'
        onClick={handlePrev}
        disabled={!canPrev}
        className={cn(
          'absolute top-1/2 -left-2 z-20 flex h-12 w-10 -translate-y-1/2 items-center justify-center min-[1300px]:-left-16',
          'hover:bg-background-contrast text-white',
          'disabled:pointer-events-none disabled:opacity-40',
        )}
      >
        <ArrowLeft size={32} />
      </button>

      {/* Right Arrow */}
      <button
        type='button'
        aria-label='Next slide'
        onClick={handleNext}
        disabled={!canNext}
        className={cn(
          'absolute top-1/2 -right-2 z-20 flex h-12 w-10 -translate-y-1/2 items-center justify-center min-[1300px]:-right-16',
          'hover:bg-background-contrast text-white',
          'disabled:pointer-events-none disabled:opacity-40',
        )}
      >
        <ArrowRight size={32} />
      </button>

      {/* Viewport + Slides */}
      <div className='overflow-hidden' ref={viewportRef}>
        <div className='flex'>{slides}</div>
      </div>

      <div
        className={cn('no-wrap mt-8 flex w-full items-center justify-center gap-4')}
        role='tablist'
      >
        {slides.map((_, index) => (
          <button
            id={`hero-carousel-tab-${index + 1}`}
            type='button'
            aria-controls={`hero-carousel-slide-${index + 1}`}
            aria-label={`Go to slide ${index + 1}`}
            aria-selected={selectedSnapIndex === index}
            className={cn(
              'h-[20px] w-[20px] rounded-full border-[1px] border-solid border-[#b3b3b3] hover:bg-[#b3b3b3]',
              selectedSnapIndex === index && 'border-primary bg-primary hover:bg-primary',
            )}
            key={index}
            onClick={() => api?.scrollTo(index)}
            role='tab'
          />
        ))}
      </div>
    </div>
  );
};
